/**
 * Shared store hours / holiday helpers for store selector + cart pickup scheduler.
 */
if ( typeof StoreHoursUtils === 'undefined' ) {

  const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const DAY_DATA_KEYS = [
    'hoursSunday',
    'hoursMonday',
    'hoursTuesday',
    'hoursWednesday',
    'hoursThursday',
    'hoursFriday',
    'hoursSaturday'
  ];

  class StoreHoursUtils {

    static #holidays = null;

    static loadHolidays() {
      if ( this.#holidays !== null ) {
        return this.#holidays;
      }
      const node = document.getElementById('store-holidays-data');
      if ( ! node ) {
        this.#holidays = [];
        return this.#holidays;
      }
      try {
        this.#holidays = JSON.parse(node.textContent) || [];
      } catch ( e ) {
        this.#holidays = [];
      }
      return this.#holidays;
    }

    static resetHolidaysCache() {
      this.#holidays = null;
    }

    static dateInTimezone(date, timezone) {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    }

    static dayIndexInTimezone(date, timezone) {
      const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short'
      }).format(date);
      const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return map[weekday] ?? date.getDay();
    }

    static dayAbbrevInTimezone(date, timezone) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short'
      }).format(date);
    }

    static compareDates(a, b) {
      if ( a === b ) return 0;
      return a < b ? -1 : 1;
    }

    static isDateInRange(localDate, startDate, endDate) {
      if ( ! startDate ) return false;
      const end = endDate || startDate;
      return this.compareDates(localDate, startDate) >= 0 && this.compareDates(localDate, end) <= 0;
    }

    static parseISODate(iso) {
      const [year, month, day] = iso.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    static toISO(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    static formatPickupDate(iso, timezone = 'Australia/Adelaide') {
      const date = this.parseISODate(iso);
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: timezone,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(date);
    }

    static addDays(date, days) {
      const next = new Date(date.getTime());
      next.setDate(next.getDate() + days);
      return next;
    }

    static formatTime12h(hourStr, minStr) {
      let hour = parseInt(hourStr, 10);
      const min = String(minStr).padStart(2, '0');
      const suffix = hour >= 12 ? 'pm' : 'am';
      hour = hour % 12;
      if ( hour === 0 ) hour = 12;

      if ( min === '00' ) {
        return `${hour}${suffix}`;
      }

      return `${hour}:${min}${suffix}`;
    }

    static formatHoursDisplay(value, dayAbbrev) {
      if ( ! value || value === 'closed' ) {
        return `${dayAbbrev}: Closed`;
      }

      const text = String(value).trim();
      const rangeMatch = text.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
      if ( rangeMatch ) {
        const open = this.formatTime12h(rangeMatch[1], rangeMatch[2]);
        const close = this.formatTime12h(rangeMatch[3], rangeMatch[4]);
        return `${dayAbbrev}: ${open}–${close}`;
      }

      const closeMatch = text.match(/^(\d{1,2}):(\d{2})$/);
      if ( closeMatch ) {
        return `${dayAbbrev}: Closes ${this.formatTime12h(closeMatch[1], closeMatch[2])}`;
      }

      return `${dayAbbrev}: ${text}`;
    }

    static hoursFromDataset(item) {
      return DAY_DATA_KEYS.map(key => item.dataset[key] || 'closed');
    }

    static hoursFromConfig(store) {
      if ( ! store?.hours ) return DAY_KEYS.map(() => 'closed');
      return DAY_KEYS.map(key => store.hours[key] || 'closed');
    }

    static isDayOpen(hoursValue) {
      return hoursValue && hoursValue !== 'closed';
    }

    static getHolidayForDate(storeHandle, localDate, holidays = null) {
      const list = holidays || this.loadHolidays();
      for ( const holiday of list ) {
        if ( ! this.isDateInRange(localDate, holiday.date, holiday.end_date) ) {
          continue;
        }
        const storeList = holiday.stores || [];
        if ( storeList.length && ! storeList.includes(storeHandle) ) {
          continue;
        }
        return holiday;
      }
      return null;
    }

    static isPickupClosedOnDate(store, date, holidays = null) {
      if ( ! store ) return true;

      const timezone = store.timezone || 'Australia/Adelaide';
      const localDate = this.dateInTimezone(date, timezone);
      const holiday = this.getHolidayForDate(store.handle, localDate, holidays);

      if ( holiday && holiday.status !== 'special_hours' ) {
        return true;
      }

      const dayIndex = this.dayIndexInTimezone(date, timezone);
      const hours = store.hours
        ? DAY_KEYS.map(key => store.hours[key] || 'closed')
        : this.hoursFromConfig(store);
      return ! this.isDayOpen(hours[dayIndex]);
    }

    static addOpenPickupDays(store, startDate, days) {
      let date = startDate;
      let remaining = Math.max(0, parseInt(days, 10) || 0);

      while ( remaining > 0 ) {
        date = this.addDays(date, 1);
        while ( this.isPickupClosedOnDate(store, date) ) {
          date = this.addDays(date, 1);
        }
        remaining -= 1;
      }

      while ( this.isPickupClosedOnDate(store, date) ) {
        date = this.addDays(date, 1);
      }

      return date;
    }

    static firstAvailablePickupDate(store, now = new Date()) {
      const timezone = store.timezone || 'Australia/Adelaide';
      const todayISO = this.dateInTimezone(now, timezone);
      const today = this.parseISODate(todayISO);
      const cutoffConfig = this.loadCutoffConfig();
      const configuredNoticeDays = parseInt(store.notice_days ?? 1, 10);
      let noticeDays = Number.isNaN(configuredNoticeDays) ? 1 : Math.max(0, configuredNoticeDays);

      if ( cutoffConfig.enabled ) {
        noticeDays = Math.max(1, noticeDays);
        if ( ! this.isOrderBeforePickupCutoff(store, now) ) {
          noticeDays += 1;
        }
      }

      return this.addOpenPickupDays(store, today, noticeDays);
    }

    static loadPickupStores() {
      const node = document.getElementById('store-pickup-data');
      if ( ! node ) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch ( e ) {
        return [];
      }
    }

    static getPickupStore(handle) {
      if ( ! handle ) return null;
      return this.loadPickupStores().find(store => store.handle === handle) || null;
    }

    static monthIndex(date) {
      return date.getFullYear() * 12 + date.getMonth();
    }

    static maxPickupDate(store, minDate = null) {
      const start = minDate || this.firstAvailablePickupDate(store);
      return this.addDays(start, 119);
    }

    static loadCutoffConfig() {
      return window.BFF_PICKUP_CUTOFF || { enabled: true, time: '15:00' };
    }

    static parse24HourTimeMinutes(value) {
      const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
      if ( ! match ) {
        return null;
      }

      const hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);

      if ( hour > 23 || minute > 59 ) {
        return null;
      }

      return hour * 60 + minute;
    }

    static parseHoursRange(value) {
      const match = String(value || '').trim().match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
      if ( ! match ) {
        return null;
      }

      const open = this.parse24HourTimeMinutes(match[1]);
      const close = this.parse24HourTimeMinutes(match[2]);
      return open === null || close === null ? null : { open, close };
    }

    static timeMinutesInTimezone(date, timezone = 'Australia/Adelaide') {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      }).formatToParts(date);
      const hour = parseInt(parts.find(part => part.type === 'hour')?.value || '0', 10);
      const minute = parseInt(parts.find(part => part.type === 'minute')?.value || '0', 10);
      return hour * 60 + minute;
    }

    static storeHoursRange(store, date = new Date()) {
      if ( ! store ) {
        return null;
      }

      const timezone = store.timezone || 'Australia/Adelaide';
      const localDate = this.dateInTimezone(date, timezone);
      const holiday = this.getHolidayForDate(store.handle, localDate);

      if ( holiday?.status === 'special_hours' ) {
        const specialRange = this.parseHoursRange(holiday.close_time);
        if ( specialRange ) {
          return specialRange;
        }

        const specialClose = this.parse24HourTimeMinutes(holiday.close_time);
        if ( specialClose !== null ) {
          const dayIndex = this.dayIndexInTimezone(date, timezone);
          const regularRange = this.parseHoursRange(this.hoursFromConfig(store)[dayIndex]);
          return regularRange ? { open: regularRange.open, close: specialClose } : null;
        }
      }

      const dayIndex = this.dayIndexInTimezone(date, timezone);
      const hours = this.hoursFromConfig(store)[dayIndex];
      return this.parseHoursRange(hours);
    }

    static isOrderBeforePickupCutoff(store, now = new Date()) {
      if ( ! store ) {
        return false;
      }

      const timezone = store.timezone || 'Australia/Adelaide';
      const config = this.loadCutoffConfig();
      if ( ! config.enabled ) {
        return true;
      }

      const currentMinutes = this.timeMinutesInTimezone(now, timezone);
      const cutoffMinutes = this.parse24HourTimeMinutes(config.time) ?? 15 * 60;
      return currentMinutes < cutoffMinutes;
    }

    static parseWindowStartMinutes(windowLabel) {
      const match = String(windowLabel).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
      if ( ! match ) {
        return null;
      }

      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2] || '0', 10);
      const suffix = match[3].toLowerCase();

      if ( suffix === 'pm' && hour < 12 ) {
        hour += 12;
      }
      if ( suffix === 'am' && hour === 12 ) {
        hour = 0;
      }

      return hour * 60 + minute;
    }

    static parseWindowEndMinutes(windowLabel) {
      const matches = [...String(windowLabel).trim().matchAll(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/gi)];
      if ( ! matches.length ) {
        return null;
      }

      const match = matches[matches.length - 1];
      let hour = parseInt(match[1], 10);
      const minute = parseInt(match[2] || '0', 10);
      const suffix = match[3].toLowerCase();

      if ( suffix === 'pm' && hour < 12 ) {
        hour += 12;
      }
      if ( suffix === 'am' && hour === 12 ) {
        hour = 0;
      }

      return hour * 60 + minute;
    }

    static filterTimeWindows(store, dateISO, windows = [], now = new Date()) {
      if ( ! windows?.length ) {
        return [];
      }

      const timezone = store?.timezone || 'Australia/Adelaide';
      const todayISO = this.dateInTimezone(now, timezone);
      const selectedDate = this.parseISODate(dateISO);
      const storeHours = this.storeHoursRange(store, selectedDate);
      const earliestISO = this.toISO(this.firstAvailablePickupDate(store, now));
      const normalizedWindows = windows.map(window => String(window).trim()).filter(Boolean);

      if ( this.isPickupClosedOnDate(store, selectedDate) ) {
        return [];
      }

      if ( dateISO < earliestISO ) {
        return [];
      }

      const currentMinutes = this.timeMinutesInTimezone(now, timezone);

      return normalizedWindows.filter(window => {
        const startMinutes = this.parseWindowStartMinutes(window);
        const endMinutes = this.parseWindowEndMinutes(window);
        if ( startMinutes === null || endMinutes === null ) {
          return true;
        }

        if ( storeHours && startMinutes < storeHours.open ) {
          return false;
        }
        if ( storeHours && endMinutes > storeHours.close ) {
          return false;
        }

        return dateISO !== todayISO || currentMinutes < endMinutes;
      });
    }

  }

  window.StoreHoursUtils = StoreHoursUtils;

}
